import React, { useContext, useRef, useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { store } from 'components/Store';
import idsWithoutImages from 'components/Dashboard/KeyUpdates/idsWithoutImages';
import { contentItemSearchQuery, locationTopicDescriptor } from 'components/Dashboard/helpers/locationSearchDescriptor';
import Share from 'components/Dashboard/components/Share';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import { useAuth0 } from '@auth0/auth0-react';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import getAppSite from 'utils/getAppSite';
import ConfigData from 'config/ConfigData';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import usePositionBodySnippet from 'components/Dashboard/hooks/usePositionBodySnippet';
import {
  createNumbers,
  compileNumberOfContributions,
} from 'components/Dashboard/StakeHolders/createStakeholderNumbers';
import extractMergedKeywordMatches from 'components/Dashboard/utilities/extractMergedKeywordMatches';

const loadImageUrl = (imageId, imageUrl) => {
  return new Promise((resolve, reject) => {
    const urlImage =
      imageUrl ??
      (imageId !== 0 && imageId !== null && !idsWithoutImages.includes(imageId)
        ? `https://www.parliamenttoday.com/image.php?imageid=${imageId}&ml=1`
        : `${import.meta.env.VITE_APP_CDNURL}/images/20210722-stakeholders-placeholder.png`);
    let base_image = new Image();
    base_image.src = urlImage;
    base_image.onload = () => {
      resolve('success');
    };
    base_image.onerror = reject;
  });
};

const StakeholderItem = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { search, activePlan } = state;
  const { enableStakeholders, enableSentiment } = activePlan ?? {};
  const { list, activeSearch } = search;

  const advancedSearch = getUrlParam('and') || getUrlParam('not') || getUrlParam('or');
  const searchQuery = getUrlParam('search') || getUrlParam('or');
  const {
    item,
    scrollBarRef,
    array,
    index,
    isContact,
    setContactIdsCheck,
    contactIdsCheck,
    setShowStakeholdersCrmPopup,
    highlights,
    openInNewTab,
    zIndex,
    isPreloaded,
  } = props;
  const {
    id,
    title,
    positionCategory,
    imageId,
    imageUrl,
    summary,
    categories,
    positions,
    aggregatedSentiment,
    tags,
    contentUrl,
    biographyHighlightedSnippet,
  } = item;
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const { searchResultsSection } = useMainSectionParameter(params);
  const { isAuthenticated } = useAuth0();

  const relevantListRef = useRef(null);
  const relevantActivitiesContainerRef = useRef(null);
  const biographyIncludesCondition = () => {
    const activeSearchCondition = activeSearch || advancedSearch || getUrlParam('search') || getUrlParam('or');
    const listCondition = list === null || location.pathname?.startsWith('/general-election');
    const propertiesCondition =
      !title?.toLowerCase().includes(searchQuery?.toLowerCase()) &&
      !positions?.some((position) => position?.toLowerCase().includes(searchQuery?.toLowerCase())) &&
      !tags.some((tag) => tag?.toLowerCase().includes(searchQuery?.toLowerCase()));

    return (
      activeSearchCondition &&
      listCondition &&
      highlights?.highlightedBodySnippets?.some((item) => item?.includes("<em class='policymogul-keyword-match'>")) &&
      propertiesCondition
    );
  };

  const positionCategoryItems = [
    'Cabinet',
    'Government',
    'Shadow Cabinet',
    'Shadow Government',
    'Parliamentary Private Secretary',
    'Special Advisor',
  ];
  const houseOfItem = (categories) => {
    if (categories.includes('MP')) {
      return 'commons';
    } else if (categories.includes('Lord')) {
      return 'lords';
    } else {
      return '';
    }
  };

  const relevantActivityQueryParam = (search) => {
    return `${searchResultsSection ? `${search?.startsWith('?') ? '&' : '?'}relevant-activity=false` : ''}`;
  };

  const locationDescriptor = (path) => {
    const createSearch = () => {
      let search;
      if (!enableStakeholders || !isAuthenticated) {
        search = '';
      } else if (searchResultsSection || !getUrlParam('topic-id')) {
        search = `${contentItemSearchQuery(location, list)}`;
      } else {
        search = `${locationTopicDescriptor(location)}`;
      }
      return `${search}${relevantActivityQueryParam(search)}`;
    };
    return {
      pathname: path,
      search: createSearch(),
      hash: '',
    };
  };

  const CreateMatchedInformation = (field, member, finalSentence) => {
    let value = item[field];
    return (
      <>
        {!!value && value.length > 0 && (
          <>
            {value.map((item, index) => (
              <li key={`item-position${index}`}>
                <span className={`icon-${member ? 'group' : 'star'}`} style={{ fontSize: member ? '8px' : '11px' }} />
                {member ? 'Member of ' : ''}
                {item} {finalSentence}
              </li>
            ))}
          </>
        )}
      </>
    );
  };

  const listConditional = list !== null && list.id !== null;
  const topicParameterCondition = !!getUrlParam('topic-id') && getUrlParam('topic-id') !== 'all';
  const isMobile = useIsMobile(768);
  const { rememberPosition } = useRememberPosition();

  const stakeholderMapping = location.pathname.includes('/stakeholder-mapping');
  const candidatesWithoutSearch = location.pathname.includes('/general-election-2024') && !getUrlParam('search');
  const { topicOrClientWord } = useTopicOrClientWord();

  const PositionItem = ({ noIcon }) => {
    return (
      <>
        {positionCategory !== null && positionCategoryItems.includes(positionCategory) && (
          <p
            className={`nowrap-item my-2 my-md-0 stakeholder-item-position stakeholder-position-${houseOfItem(
              categories
            )} ${
              !noIcon ? `mr-4 stakeholder-position-${normalize(positionCategory)}-${houseOfItem(categories)}` : 'pt-1'
            }`}
            style={noIcon ? { fontSize: '0.95rem', marginRight: '12px' } : undefined}
          >
            {positionCategory === 'Parliamentary Private Secretary'
              ? 'Parliamentary Private Secretaries'
              : positionCategory}
          </p>
        )}
      </>
    );
  };

  const biographyItem = () => {
    return (
      <ul className='px-0 policy-list stake-list-information'>
        {(activeSearch || stakeholderMapping || candidatesWithoutSearch) && (
          <li className={`my-1 mt-1 stake-information-bio stake-information-bio-list`}>{summary}</li>
        )}
      </ul>
    );
  };

  const titleBodyComponent = useRef(null);
  const { bodySnippet } = usePositionBodySnippet({ titleBodyComponent, item: { ...item, highlights } });
  const specialAdviser = positionCategory === 'Special Adviser';
  const totalStakeholderContributions = () => {
    let values = ['parliamentaryContribution', 'writtenQuestionsAsked', 'edm', 'edmSupport'];
    if (import.meta.env.VITE_APP_ENABLE_TWITTER === 'true') {
      values = [...values, 'tweet', 'retweet'];
    }
    return compileNumberOfContributions(item, values);
  };

  const totalStakeholderContributionsValue = useMemo(() => totalStakeholderContributions(), [item]);
  const bioIncludesKeywords = useMemo(
    () => extractMergedKeywordMatches(biographyHighlightedSnippet),
    [biographyHighlightedSnippet]
  );

  return (
    <div
      className={`${stakeholderMapping ? 'position-relative py-2 stakeholder-list-item-container' : ''} ${
        isContact ? 'isContact' : ''
      }`}
      onClick={() => {
        if (openInNewTab) {
          window.open(locationDescriptor(`/stakeholders/${id}/${normalize(title)}`), '_blank');
        } else {
          navigate(locationDescriptor(`/stakeholders/${id}/${normalize(title)}`));
        }
      }}
    >
      <Link
        className='content-item-link'
        to={locationDescriptor(contentUrl)}
        style={{ zIndex: zIndex ?? (array ? array.length - index : 1) }}
        onClick={(e) => {
          e.stopPropagation();
          rememberPosition('Stakeholders', scrollBarRef, index);
        }}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
      >
        <li
          className={`content-item stakeholder-list-item ${
            stakeholderMapping ? 'stakeholder-mapping-list-item' : 'my-2'
          }`}
        >
          <div className='row mx-0 p-3 align-items-start justify-content-between'>
            {!isMobile && (
              <StakeholderImage
                imageId={imageId}
                imageUrl={imageUrl}
                index={index}
                candidatesWithoutSearch={candidatesWithoutSearch}
                isPreloaded={isPreloaded}
              />
            )}
            <div className={`content-title ${stakeholderMapping ? '' : 'pl-md-4'} stakeholder-title`}>
              <div
                className={`stakeholder-title-name-position ${
                  stakeholderMapping
                    ? listConditional || topicParameterCondition
                      ? 'section-title-mapping'
                      : ''
                    : candidatesWithoutSearch
                      ? ''
                      : 'section-title pb-2'
                }  mb-2`}
              >
                <div className='flex-centered pb-2 pb-md-0'>
                  {isMobile && (
                    <StakeholderImage
                      imageId={imageId}
                      index={index}
                      candidatesWithoutSearch={candidatesWithoutSearch}
                      isPreloaded={isPreloaded}
                    />
                  )}
                  <div className='d-flex align-items-start flex-grow-1 pl-3 pl-md-0'>
                    <div className='flex-grow-1 '>
                      <div className='d-flex align-items-center align-items-lg-start justify-content-between'>
                        <div>
                          <h4
                            className={`content-item-title mb-0 ${
                              stakeholderMapping && !isMobile ? 'nowrap-item' : 'flex-grow-1'
                            }`}
                            style={{
                              width: stakeholderMapping && !isMobile ? 'min-content' : undefined,
                            }}
                          >
                            {title}
                          </h4>
                          {!isMobile && <>{biographyItem()}</>}
                        </div>
                        {stakeholderMapping && !isMobile && <PositionItem noIcon />}
                        {isMobile && (
                          <Share
                            item={item}
                            listItem
                            alreadyInContacts={isContact}
                            stakeholderMapping={stakeholderMapping}
                            setContactIdsCheck={setContactIdsCheck}
                            contactIdsCheck={contactIdsCheck}
                            setShowStakeholdersCrmPopup={setShowStakeholdersCrmPopup}
                          />
                        )}
                      </div>
                      {isMobile && <>{biographyItem()}</>}
                    </div>
                    {!isMobile && (
                      <div className={`d-flex justify-content-end align-items-center px-0`}>
                        {!stakeholderMapping && !candidatesWithoutSearch && <PositionItem />}
                        <Share
                          item={item}
                          listItem
                          alreadyInContacts={isContact}
                          stakeholderMapping={stakeholderMapping}
                          setContactIdsCheck={setContactIdsCheck}
                          contactIdsCheck={contactIdsCheck}
                          setShowStakeholdersCrmPopup={setShowStakeholdersCrmPopup}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className={`d-flex align-items-center position-relative`} ref={relevantActivitiesContainerRef}>
                <ul
                  ref={relevantListRef}
                  className={`px-0 policy-list  ${
                    stakeholderMapping ? 'stake-list-information-mapping' : ''
                  } stake-list-information`}
                >
                  {isMobile && !candidatesWithoutSearch && <PositionItem />}
                  {!activeSearch && !candidatesWithoutSearch && !topicParameterCondition && (
                    <li className={`mb-1`}>
                      <span className={`icon-person specific-right-margin-8`} style={{ fontSize: '11px' }} /> {summary}
                    </li>
                  )}
                  {((activeSearch && listConditional) || topicParameterCondition) && (
                    <>
                      {CreateMatchedInformation('keywordMatchedPositions')}
                      {CreateMatchedInformation('keywordMatchedAppgs', true, 'APPG')}
                      {CreateMatchedInformation('keywordMatchedSelectCommittees', true, 'Select Committee')}
                      {CreateMatchedInformation('keywordMatchedLordSelectCommittees', true, 'Lords Select Committee')}
                      {createNumbers(
                        item,
                        ['parliamentaryContribution'],
                        ConfigData.icons.parliament,
                        `spoken ${getAppSite() === 'usa' ? 'congressional' : 'parliamentary'} contribution`
                      )}
                      {createNumbers(
                        item,
                        ['writtenQuestionsAsked'],
                        ConfigData.icons.parliament,
                        `written ${getAppSite() === 'usa' ? 'congressional' : 'parliamentary'} contribution`
                      )}
                      {createNumbers(
                        item,
                        ['edm'],
                        ConfigData.icons.parliament,
                        'Early Day Motion',
                        'as primary sponsor'
                      )}
                      {createNumbers(
                        item,
                        ['edmSupport'],
                        ConfigData.icons.parliament,
                        'Early Day Motion',
                        'supported'
                      )}
                      {import.meta.env.VITE_APP_ENABLE_TWITTER === 'true' && (
                        <>{createNumbers(item, ['tweet', 'retweet'], `twitter-x`, 'X post')}</>
                      )}
                      {aggregatedSentiment && enableSentiment && (
                        <li className={`mb-0 sentiment-stakeholder-list`}>
                          <span
                            className={`d-inline-block sentiment-dot sentiment-dot-${aggregatedSentiment?.toLowerCase()}`}
                          />
                          {specialAdviser
                            ? 'Sentiment is not available for Special Advisers'
                            : `Sentiment analysis for this ${topicOrClientWord}: ${totalStakeholderContributionsValue === 0 ? 'Not enough data available' : aggregatedSentiment}`}
                        </li>
                      )}
                    </>
                  )}
                  {activeSearch && list === null && (
                    <>
                      {positions.length > 0 && !specialAdviser && (
                        <>
                          {positions.map((item, index) => (
                            <li key={`item-position${index}`}>
                              <span className={`icon-star`} style={{ fontSize: '11px' }} />
                              {item}
                            </li>
                          ))}
                        </>
                      )}
                    </>
                  )}
                  {biographyIncludesCondition() ? (
                    <>
                      <li className='mb-0'>
                        <span className={`icon-profile`} style={{ fontSize: '11px' }} />
                        Biography includes search term:{' '}
                        <p
                          className='title-body mb-0 position-relative specific-left-padding-20 specific-right-padding-50 line-height-1-5'
                          ref={titleBodyComponent}
                        >
                          <span dangerouslySetInnerHTML={createMarkup(bodySnippet)} />
                        </p>
                      </li>
                    </>
                  ) : (
                    <>
                      {bioIncludesKeywords.length > 0 && (
                        <li className='mt-2 biography-includes-keywords'>
                          <span className={`icon-profile`} style={{ fontSize: '11px' }} />
                          Biography includes{' '}
                          {bioIncludesKeywords.length >= 3
                            ? 'multiple keywords from this topic'
                            : bioIncludesKeywords.length === 2
                              ? `"${bioIncludesKeywords[0]}" and "${bioIncludesKeywords[1]}"`
                              : bioIncludesKeywords.length === 1
                                ? `"${bioIncludesKeywords[0]}"`
                                : 'keywords from this topic'}
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </li>
      </Link>
    </div>
  );
};

const StakeholderImage = (props) => {
  const { imageId, imageUrl, candidatesWithoutSearch, isPreloaded } = props;
  const componentMounted = useRef(true);

  const [loadingImage, setLoadingImage] = useState(!isPreloaded);
  const loadImage = useRef(null);
  loadImage.current = async () => {
    await loadImageUrl(imageId, imageUrl);
    if (componentMounted.current) {
      setLoadingImage(false);
    }
  };

  useEffect(() => {
    componentMounted.current = true;
    if (!isPreloaded) {
      loadImage.current();
    }
    return () => {
      componentMounted.current = false;
    };
  }, [isPreloaded]);
  const isMobile = useIsMobile();
  const size = isMobile ? 65 : 95;

  return (
    <>
      {loadingImage ? (
        <SkeletonTheme baseColor='#f2f2f2'>
          <Skeleton circle={true} height={size} width={size} />
        </SkeletonTheme>
      ) : (
        <div
          className={`stakeholder-profile-image stakeholder-list-profile-image ${
            candidatesWithoutSearch ? 'candidate-profile-image' : ''
          }`}
        >
          <img
            className='w-100 rounded-circle h-100'
            style={{ objectFit: 'cover', maxHeight: `${size}px` }}
            src={
              imageUrl ??
              (imageId !== 0 && imageId !== null && !idsWithoutImages.includes(imageId)
                ? `https://www.parliamenttoday.com/image.php?imageid=${imageId}&ml=1`
                : `${import.meta.env.VITE_APP_CDNURL}/images/20210722-stakeholders-placeholder.png`)
            }
          />
        </div>
      )}
    </>
  );
};

export { StakeholderImage };
export default StakeholderItem;
